/**
 * PreSendConfirmationModal — final guard before a flight transitions out of
 * draft. Loads the live audience count, validates unmapped template
 * variables, and blocks the schedule action if any of these fail:
 *
 *   - audience-count endpoint errors out
 *   - audience count is 0
 *   - any required template merge_field has no mapping AND is not in the
 *     system-fallback set { email, first_name, last_name }
 *   - any sourceType='custom' mapping has empty customValue
 *
 * Operator clicks Confirm → caller's onConfirm runs (typically
 * updateFlight + status change). Cancel closes without side effects.
 *
 * Phase 1B (2026-05-08).
 */

import { useState, useEffect, useMemo } from "react";
import { supabase } from "../../services/supabase";
import { useTemplateGuardEnabled } from "../../hooks/useTemplateGuardEnabled";
import { summarizeAudienceStats } from "../../utils/audienceExclusions";
import {
  getSendGridTemplateDetails,
  SYSTEM_FALLBACK_FIELDS,
  SENDGRID_AUTO_INJECTED_FIELDS,
  computeUnmappedTemplateVariables,
  detectForbiddenUnsubscribeTokens,
  forbiddenUnsubscribeMessage,
} from "../../services/templates.service";
import { formatScheduledDateTime } from "../../utils/formatters";
import { getFlightEmailCheckStatus } from "../../services/campaigns.service";
import {
  templateUsesInviteLink,
  willTokenizeInviteLink,
} from "../../utils/inviteLinkTokenization";
import type { Flight, FieldMapping } from "../../types/campaign.types";
import type { SendGridTemplateDetails } from "../../types/sendgrid.types";
import {
  validateLandingConfig,
  requiredLandingVariables,
  type LandingConfig,
} from "../../services/landingLink";

interface AudienceCountResponse {
  success: boolean;
  stats?: {
    originalCount: number;
    eligibleCount: number;
    excludedCount: number;
    /** Phase 4 (2026-05-16): contacts dropped by the email-suppression
     *  filter. Independent of excludedCount. Reported by the server so
     *  the modal shows server truth, not a frontend env assumption. */
    suppressedCount: number;
    /** True when the suppression kill-switch (DISABLE_EMAIL_SUPPRESSION_
     *  FILTER env var) is active. When true, suppressedCount === 0
     *  because no drops happened. The modal renders a yellow warning. */
    suppressionFilterDisabled?: boolean;
    /** Always-on invalid guard + per-flight risky opt-out (count parity with send). */
    invalidEmailDroppedCount?: number;
    riskyEmailDroppedCount?: number;
    excludeRisky?: boolean;
    /** Verdict composition of the audience (post-suppression base). */
    verdictBreakdown?: {
      valid?: number;
      risky?: number;
      invalid?: number;
      unvalidated?: number;
    };
    /** Visibility breakdown (2026-06-24, additive). Per-reason exclusion counts
     *  + bounded examples for small audiences. May be absent on legacy payloads. */
    rawAudienceCount?: number;
    excludedTotal?: number;
    reasons?: {
      email_opt_out?: number;
      suppressed?: number;
      missing_email?: number;
      neverbounce_invalid?: number;
      neverbounce_risky?: number;
      audience_filter?: number;
    };
    examples?: { email: string; reason: string }[];
    examplesOmitted?: boolean;
  };
  audience_filters_applied?: boolean;
  error?: string;
}

// Lookup coverage — same shape the flight-lookup-coverage endpoint
// returns. See supabase/functions/flight-lookup-coverage/index.ts.
interface LookupCoverageEntry {
  mapping_template_field: string;
  lookup_id: string | null;
  resolution_mode: "keyed" | "default_row";
  lookup_exists: boolean;
  value_column_exists: boolean;
  contact_key_field_exists: boolean;
  default_row_exists: boolean;
  filter_columns_exist: boolean[];
  matched: number;
  filter_excluded: number;
  default_used: number;
  fallback_used: number;
  empty_key: number;
}

// Landing-link coverage — one row per contact variable the personalized
// landing link depends on. See flight-lookup-coverage/index.ts.
interface LandingLinkCoverageEntry {
  variable: string;
  known: boolean;
  empty: number;
}

interface LookupCoverageResponse {
  success: boolean;
  audience_count?: number;
  lookup_coverage?: LookupCoverageEntry[];
  landing_link_coverage?: LandingLinkCoverageEntry[];
  error?: string;
  // The function's own failures return `error`. But when the PLATFORM kills the
  // worker — HTTP 546 WORKER_RESOURCE_LIMIT, seen 2026-09-21 on a 36k-contact
  // Partner Newsletter flight — the function never reaches its catch, and the
  // body is the gateway's envelope instead: {code, message}. Reading only
  // `error` rendered that as the useless "unknown error", which is what the
  // operator reported. Carry both so the real reason survives.
  message?: string;
  code?: string;
}

interface PreSendConfirmationModalProps {
  isOpen: boolean;
  flight: Flight;
  listName?: string;
  onConfirm: () => void;
  onClose: () => void;
  /**
   * 'schedule' (default): operator is scheduling for later. Confirm button
   *                       reads "Confirm and schedule"; the scheduled-send
   *                       time and date is shown.
   * 'send_now':           operator is firing immediately. Confirm button
   *                       reads "Confirm and send now". The scheduled-send
   *                       date in the body becomes "Send time: immediate"
   *                       so the operator doesn't think they're queuing
   *                       a future send.
   * Added 2026-05-13 for the Send Now button on FlightDetail.
   */
  mode?: "schedule" | "send_now";
}

const PreSendConfirmationModal: React.FC<PreSendConfirmationModalProps> = ({
  isOpen,
  flight,
  listName,
  onConfirm,
  onClose,
  mode = "schedule",
}) => {
  const [audienceLoading, setAudienceLoading] = useState(false);
  const [audienceResult, setAudienceResult] =
    useState<AudienceCountResponse | null>(null);

  // Cached count for dynamic segments. resolveAudience throws for
  // is_dynamic=true lists (campaign flights don't support dynamic sends
  // yet), so the audience-count call above fails as a hard block. We
  // ALSO load lists.contact_count + last_calculated separately so the
  // operator can see the size of the audience they're trying to use,
  // even though the schedule action stays blocked. Display-only —
  // intentionally does NOT clear blockers.
  const [cachedListInfo, setCachedListInfo] = useState<{
    is_dynamic: boolean;
    contact_count: number | null;
    last_calculated: string | null;
  } | null>(null);

  // Lookup coverage — only fetched when the flight has any
  // sourceType='lookup' mappings. Result feeds both the inline display
  // and the blockers list (red validation flags hard-block schedule).
  const [lookupCoverageLoading, setLookupCoverageLoading] = useState(false);
  const [lookupCoverageResult, setLookupCoverageResult] =
    useState<LookupCoverageResponse | null>(null);
  // #B1: operator acknowledgement that recipients missing a REQUIRED landing value will be
  // excluded from this flight (they are isolated at send time, not sent).
  const [exclusionAck, setExclusionAck] = useState(false);

  // Template details fetch state. The modal owns this fetch (rather than
  // the parent passing pre-fetched details) so a fetch failure becomes a
  // BLOCKING state rather than silently leaving the unmapped-variable
  // check passing on null merge_fields. Prior bug: parent treated the
  // fetch as best-effort, modal received null, unmappedVariables returned
  // empty, operator could schedule a flight whose template might have
  // unmapped variables.
  const [emailTemplate, setEmailTemplate] =
    useState<SendGridTemplateDetails | null>(null);
  const [emailTemplateLoading, setEmailTemplateLoading] = useState(false);
  const [emailTemplateError, setEmailTemplateError] = useState<string | null>(
    null,
  );

  // EIB silent-blast guard (the authoritative one — this modal is the single
  // chokepoint for Send Now / Schedule / Edit Schedule). The send path only
  // injects a personalized (tokenized) {{invite_link}} when the flight's
  // effective offer is a $0 placeholder bound to an event whose
  // registration_mode === 'invite_gated' (send-campaign-flight ~L200-231).
  // A template that uses {{invite_link}} in any other context renders it EMPTY
  // to the whole audience. Unlike {{landing_page_link}} (which has the flight's
  // own landing-page config as a fallback), {{invite_link}} has NO fallback, so
  // an invite_link template that won't tokenize is always a blast. We resolve
  // willTokenize by mirroring the send path exactly and FAIL CLOSED here: while
  // resolving or on any error, the confirm button stays blocked.
  const [inviteTokenization, setInviteTokenization] = useState<{
    loaded: boolean;
    willTokenize: boolean;
    errored: boolean;
  }>({ loaded: false, willTokenize: false, errored: false });

  // ASM template-guard flag (FG-B1): gates the dead-unsubscribe-link block below. OFF (default)
  // during deploy-dark → informational warning only; ON → hard blocker. Declared here with the
  // other hooks — BEFORE any early return — so hook order is stable (rules-of-hooks).
  const templateGuardEnabled = useTemplateGuardEnabled();

  // Variables auto-injected by the send path that don't need an explicit
  // mapping. `subject` is set by the per-flight subject override (or
  // SendGrid template default), `preheader` only when metadata.preheader
  // is set on this flight. Computed per-flight because preheader
  // injection is conditional.
  const autoInjectedVariables = useMemo<Set<string>>(() => {
    // SYSTEM_FALLBACK_FIELDS covers contact-column fallbacks.
    // SENDGRID_AUTO_INJECTED_FIELDS covers SendGrid-resolved template
    // variables (unsubscribe, weblink, sender_*) that operators
    // legitimately leave unmapped. Keep this set identical to
    // FlightForm's autoInjected set so the strict-on-save gate and the
    // schedule-button gate cannot disagree.
    const set = new Set<string>([
      ...SYSTEM_FALLBACK_FIELDS,
      ...SENDGRID_AUTO_INJECTED_FIELDS,
    ]);
    // subject is always sent (via dynamic_template_data.subject) when the
    // operator has set flight.email_subject. Otherwise SendGrid uses the
    // template default; either way `{{subject}}` resolves.
    if (flight.email_subject && flight.email_subject.trim()) {
      set.add("subject");
    }
    // preheader is only injected when the flight has metadata.preheader set.
    const meta = flight.metadata as Record<string, unknown> | undefined;
    if (
      meta?.preheader &&
      typeof meta.preheader === "string" &&
      meta.preheader.trim()
    ) {
      set.add("preheader");
    }
    return set;
  }, [flight.email_subject, flight.metadata]);

  // Unmapped-variable check is pure (just compares two arrays). Computed
  // synchronously from the loaded emailTemplate + flight.email_template_data.
  // Returns null when we cannot determine the answer (template failed to
  // load); caller treats null as a hard blocker. Shared with FlightForm's
  // strict-on-save gate via computeUnmappedTemplateVariables — keep the
  // two surfaces using the same helper so a flight that saves cleanly
  // can't be silently refused at schedule time.
  const unmappedVariables = useMemo<string[] | null>(() => {
    if (emailTemplateLoading) {
      return null;
    }
    if (emailTemplateError) {
      return null;
    }
    if (!emailTemplate?.merge_fields) {
      return null;
    }
    const mappings: FieldMapping[] =
      flight.email_template_data?.field_mappings ?? [];
    return computeUnmappedTemplateVariables(
      emailTemplate.merge_fields,
      mappings,
      autoInjectedVariables,
    );
  }, [emailTemplate, flight.email_template_data, autoInjectedVariables]);

  // Does the selected template use {{invite_link}}? Derived synchronously from
  // the (already-fetched) template so there's no render tick where the confirm
  // button is briefly enabled before the async tokenization check has run.
  const hasInviteLink = useMemo<boolean>(
    () => templateUsesInviteLink(emailTemplate?.merge_fields),
    [emailTemplate],
  );

  // Load cached list metadata (is_dynamic + contact_count + last_calculated)
  // when the modal opens. Used to surface a display-only cached count when
  // resolveAudience rejects a dynamic-segment flight.
  useEffect(() => {
    if (!isOpen || !flight.list_id) {
      setCachedListInfo(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("lists")
        .select("is_dynamic, contact_count, last_calculated")
        .eq("id", flight.list_id!)
        .maybeSingle();
      if (cancelled) {
        return;
      }
      if (error || !data) {
        setCachedListInfo(null);
        return;
      }
      setCachedListInfo({
        is_dynamic: data.is_dynamic,
        contact_count: data.contact_count,
        last_calculated: data.last_calculated,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, flight.list_id]);

  // Is the automatic NeverBounce check on for THIS flight (switch on + flight in scope)? Decides whether the
  // "not yet checked" count may promise "checked automatically before sending" (audit a44d5e07 P2). Unknown
  // (null) or a failed read = do not promise it.
  const [autoCheckEnabled, setAutoCheckEnabled] = useState<boolean | null>(
    null,
  );
  useEffect(() => {
    if (!isOpen || !flight.id) {
      setAutoCheckEnabled(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const st = await getFlightEmailCheckStatus(flight.id);
        if (!cancelled) {
          setAutoCheckEnabled(st?.enabled === true);
        }
      } catch {
        if (!cancelled) {
          setAutoCheckEnabled(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, flight.id]);

  // Fetch the live audience count whenever the modal opens.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    // #B1: re-require acknowledgement each time the modal opens (fresh coverage each open).
    setExclusionAck(false);
    setAudienceResult(null);
    setAudienceLoading(true);
    (async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const accessToken = sessionData.session?.access_token;
        if (!accessToken) {
          throw new Error("Not authenticated");
        }
        const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/flight-audience-count`;
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ flight_id: flight.id }),
        });
        const data: AudienceCountResponse = await response.json();
        setAudienceResult(data);
      } catch (err) {
        setAudienceResult({
          success: false,
          error: (err as Error).message,
        });
      } finally {
        setAudienceLoading(false);
      }
    })();
  }, [isOpen, flight.id]);

  // Fetch coverage when the modal opens if the flight uses either
  // sourceType='lookup' mappings OR an enabled landing_page_config (the
  // endpoint reports both lookup_coverage and landing_link_coverage). Skips
  // the round-trip when neither applies.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const mappings: FieldMapping[] =
      flight.email_template_data?.field_mappings ?? [];
    const hasLookupMappings = mappings.some((m) => m.sourceType === "lookup");
    const landingCfg = flight.metadata?.landing_page_config as
      | { enabled?: boolean; base_url?: string }
      | undefined;
    const hasLandingConfig = Boolean(
      landingCfg?.enabled && landingCfg?.base_url,
    );
    if (!hasLookupMappings && !hasLandingConfig) {
      setLookupCoverageResult(null);
      setLookupCoverageLoading(false);
      return;
    }
    setLookupCoverageResult(null);
    setLookupCoverageLoading(true);
    let cancelled = false;
    (async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const accessToken = sessionData.session?.access_token;
        if (!accessToken) {
          throw new Error("Not authenticated");
        }
        const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/flight-lookup-coverage`;
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ flight_id: flight.id }),
        });
        const data: LookupCoverageResponse = await response.json();
        if (!cancelled) {
          // A non-2xx is a failure even when the body forgot to say so. The
          // gateway's WORKER_RESOURCE_LIMIT envelope has no `success` key at
          // all, so without this the modal would read `success: undefined` as
          // falsy and fall through to the no-reason branch.
          setLookupCoverageResult(
            response.ok
              ? data
              : {
                  ...data,
                  success: false,
                  error:
                    data.error ??
                    [data.code, data.message].filter(Boolean).join(": ") ??
                    undefined,
                },
          );
        }
      } catch (err) {
        if (!cancelled) {
          setLookupCoverageResult({
            success: false,
            error: (err as Error).message,
          });
        }
      } finally {
        if (!cancelled) {
          setLookupCoverageLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, flight.id, flight.email_template_data]);

  // Fetch the email template's merge_fields whenever the modal opens. If
  // this fails the modal will hard-block the schedule action so we never
  // proceed with an unknown unmapped-variable surface.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (!flight.email_template_id) {
      setEmailTemplate(null);
      setEmailTemplateError("Flight has no email template configured");
      setEmailTemplateLoading(false);
      return;
    }
    setEmailTemplate(null);
    setEmailTemplateError(null);
    setEmailTemplateLoading(true);
    let cancelled = false;
    (async () => {
      try {
        const details = await getSendGridTemplateDetails(
          flight.email_template_id as string,
        );
        if (cancelled) {
          return;
        }
        setEmailTemplate(details);
      } catch (err) {
        if (cancelled) {
          return;
        }
        setEmailTemplateError(
          (err as Error).message || "Failed to load template",
        );
      } finally {
        if (!cancelled) {
          setEmailTemplateLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, flight.email_template_id]);

  // Resolve whether the send path WILL tokenize {{invite_link}} for this flight.
  // Mirrors send-campaign-flight EXACTLY: voucherLookupFlightId =
  // voucher_source_flight_id ?? flight.id (reminders resolve the offer off the
  // source flight); campaign_flights.offer_id -> offers(is_placeholder,event_id)
  // -> event_ticketing_provisioning.registration_mode === 'invite_gated'. Only
  // runs when the template actually uses {{invite_link}}; fails CLOSED (errored)
  // so a lookup failure blocks the send rather than allowing an empty blast.
  useEffect(() => {
    if (!isOpen || !hasInviteLink) {
      // Not applicable — mark loaded so the (hasInviteLink-gated) blocker is a
      // no-op. When hasInviteLink is false the blocker never reads willTokenize.
      setInviteTokenization({
        loaded: true,
        willTokenize: false,
        errored: false,
      });
      return;
    }
    let cancelled = false;
    setInviteTokenization({
      loaded: false,
      willTokenize: false,
      errored: false,
    });
    (async () => {
      try {
        const lookupFlightId = flight.voucher_source_flight_id ?? flight.id;
        const { data: lookupFlight, error: lfErr } = await supabase
          .from("campaign_flights")
          .select("offer_id")
          .eq("id", lookupFlightId)
          .maybeSingle();
        if (lfErr) {
          throw lfErr;
        }
        let offer: {
          is_placeholder?: boolean | null;
          event_id?: string | null;
        } | null = null;
        let prov: { registration_mode?: string | null } | null = null;
        if (lookupFlight?.offer_id) {
          const { data: offerData, error: oErr } = await supabase
            .from("offers")
            .select("is_placeholder, event_id")
            .eq("id", lookupFlight.offer_id)
            .maybeSingle();
          if (oErr) {
            throw oErr;
          }
          offer = offerData;
          if (offer?.is_placeholder === true && offer?.event_id) {
            const { data: provData, error: pErr } = await supabase
              .from("event_ticketing_provisioning")
              .select("registration_mode")
              .eq("event_id", offer.event_id as string)
              .maybeSingle();
            if (pErr) {
              throw pErr;
            }
            prov = provData;
          }
        }
        if (!cancelled) {
          setInviteTokenization({
            loaded: true,
            willTokenize: willTokenizeInviteLink(offer, prov),
            errored: false,
          });
        }
      } catch (err) {
        console.error("invite_link tokenization check failed:", err);
        // Fail CLOSED: an invite_link template whose context we can't verify
        // must not send (it would blast empty links). errored -> hard blocker.
        if (!cancelled) {
          setInviteTokenization({
            loaded: true,
            willTokenize: false,
            errored: true,
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, hasInviteLink, flight.id, flight.voucher_source_flight_id]);

  if (!isOpen) {
    return null;
  }

  const audienceCount = audienceResult?.stats?.eligibleCount ?? null;
  const audienceCountFailed =
    !audienceLoading &&
    (!audienceResult?.success ||
      audienceCount === null ||
      audienceCount === undefined);
  const audienceCountZero =
    !audienceLoading && audienceResult?.success && audienceCount === 0;

  // Block confirmation if ANY blocking condition is true.
  const blockers: string[] = [];
  if (audienceLoading) {
    blockers.push("Audience count loading…");
  } else if (audienceCountFailed) {
    blockers.push(
      audienceResult?.error
        ? `Audience count failed: ${audienceResult.error}`
        : "Audience count failed",
    );
  } else if (audienceCountZero) {
    blockers.push(
      "Audience count is 0 — no contacts will receive this flight.",
    );
  }

  // Template-fetch state — treated as a hard block when we can't determine
  // unmapped variables. unmappedVariables === null signals "we don't know"
  // (loading or fetch failed); never silently allow scheduling in that case.
  if (emailTemplateLoading) {
    blockers.push("Email template loading…");
  } else if (emailTemplateError) {
    blockers.push(`Email template failed to load: ${emailTemplateError}`);
  } else if (unmappedVariables === null) {
    blockers.push(
      "Email template details unavailable — cannot validate unmapped variables.",
    );
  } else if (unmappedVariables.length > 0) {
    blockers.push(
      `Unmapped template variables: ${unmappedVariables.join(", ")}`,
    );
  }

  // Broken legacy unsubscribe tokens (audit R2-H2): block the send if the template
  // uses {{unsubscribe}} / {{unsubscribe_preferences}} — those render an empty link on a
  // CRM (API) send. Same detection as FlightForm's save gate so the two can't disagree.
  // FLAG-GATED (audit FG-B1): only a hard blocker when the guard is ON; while OFF (deploy-dark)
  // the send proceeds and an informational migration warning is shown (see landingWarnings).
  if (templateGuardEnabled && emailTemplate?.merge_fields) {
    const forbidden = detectForbiddenUnsubscribeTokens(
      emailTemplate.merge_fields,
    );
    if (forbidden.length > 0) {
      blockers.push(forbiddenUnsubscribeMessage(forbidden));
    }
  }

  // EIB silent-blast guard — only relevant when the template uses {{invite_link}}.
  // Fail closed: block while the tokenization check is resolving, block on error,
  // and block when the flight won't tokenize (empty-link blast).
  if (hasInviteLink) {
    if (!inviteTokenization.loaded) {
      blockers.push("Verifying invite-link configuration…");
    } else if (inviteTokenization.errored) {
      blockers.push(
        "Couldn't verify this flight's invite-link setup — not sending, to avoid delivering empty {{invite_link}} links. Try again in a moment.",
      );
    } else if (!inviteTokenization.willTokenize) {
      blockers.push(
        "This template uses {{invite_link}}, but this flight won't generate personalized invite links — its offer isn't an admission ticket for an invite-gated event, so every recipient would get an empty link. Attach the event's ticket offer and set the event's registration to invite-gated, or use a template without {{invite_link}}.",
      );
    }
  }

  // Lookup coverage — block schedule on any red flag. The endpoint
  // returns lookup_exists / value_column_exists / contact_key_field_exists
  // for each mapping plus a per-rule filter_columns_exist[] array. Any
  // false here means the mapping would silently render fallback (or
  // empty) at send time — and the marketer probably doesn't realize.
  if (lookupCoverageLoading) {
    blockers.push("Lookup coverage loading…");
  } else if (lookupCoverageResult && !lookupCoverageResult.success) {
    const reason =
      lookupCoverageResult.error ||
      [lookupCoverageResult.code, lookupCoverageResult.message]
        .filter(Boolean)
        .join(": ") ||
      "unknown error";
    blockers.push(`Lookup coverage failed: ${reason}`);
  } else if (lookupCoverageResult?.lookup_coverage) {
    const totalAudience = lookupCoverageResult.audience_count ?? 0;
    // Map templateField → mapping so a 0%-match blocker can name the actual
    // contact key field / lookup value column it's pointing at.
    const mappingByField = new Map<string, FieldMapping>(
      (flight.email_template_data?.field_mappings ?? [])
        .filter((m: FieldMapping) => m.templateField)
        .map((m: FieldMapping) => [m.templateField as string, m]),
    );
    for (const c of lookupCoverageResult.lookup_coverage) {
      if (!c.lookup_exists) {
        blockers.push(
          `Lookup mapping {{${c.mapping_template_field}}} references a deleted lookup. Edit the variable mapping before sending.`,
        );
        continue;
      }
      if (!c.value_column_exists) {
        blockers.push(
          `Lookup mapping {{${c.mapping_template_field}}} references a column that no longer exists in the lookup.`,
        );
      }
      if (!c.contact_key_field_exists) {
        blockers.push(
          `Lookup mapping {{${c.mapping_template_field}}} references a contact field that doesn't exist on contacts.`,
        );
      }
      if (c.resolution_mode === "default_row" && !c.default_row_exists) {
        blockers.push(
          `Lookup bundle {{${c.mapping_template_field}}} has no default row. Open the lookup and mark exactly one row with the star before sending.`,
        );
      }
      const badFilterColumns = c.filter_columns_exist
        .map((ok, idx) => (ok ? null : idx + 1))
        .filter((n): n is number => n !== null);
      if (badFilterColumns.length > 0) {
        blockers.push(
          `Lookup mapping {{${c.mapping_template_field}}} has filter rule${badFilterColumns.length === 1 ? "" : "s"} #${badFilterColumns.join(", ")} referencing missing column${badFilterColumns.length === 1 ? "" : "s"}.`,
        );
      }
      // 0%-coverage block: the mapping is structurally valid (lookup, value
      // column, contact key field, and filter columns all exist), yet it
      // matches NO recipient — so every recipient silently gets the
      // fallback/default and the lookup is effectively doing nothing. This is
      // almost always a wrong contact-key-field or unpopulated audience data
      // (the value in the contact key field must equal a key in the lookup).
      const structurallyValid =
        c.value_column_exists &&
        c.contact_key_field_exists &&
        c.filter_columns_exist.every(Boolean);
      if (
        c.resolution_mode !== "default_row" &&
        structurallyValid &&
        totalAudience > 0 &&
        c.matched === 0
      ) {
        const m = mappingByField.get(c.mapping_template_field);
        const keyField = m?.lookupKeyContactField;
        const valueCol = m?.lookupValueColumn;
        let detail = keyField
          ? ` It matches each contact's "${keyField}" field against the lookup's key column${valueCol ? ` (returning "${valueCol}")` : ""} — the value in "${keyField}" must exactly equal a key in the lookup table.`
          : ` The value in the contact key field must exactly equal a key in the lookup table.`;
        if (keyField === "membership_type") {
          detail += ` Note: "membership_type" holds values like Member/Secondary, not partner organization names, so it will never match partner-name keys (e.g. DSSA / VCDL). Point the mapping at a contact field that holds the partner org name.`;
        }
        blockers.push(
          `Lookup mapping {{${c.mapping_template_field}}} matches 0 of ${totalAudience} recipients — every recipient would get the fallback/default, so this lookup is not working.${detail}`,
        );
      }
    }
  }

  // Landing-config validation — authoritative for BOTH schedule and send-now
  // (the modal is the single chokepoint). A saved draft can hold an invalid
  // config; block the transition on it here (the backend send also re-validates).
  const persistedLandingConfig = flight.metadata?.landing_page_config as
    | LandingConfig
    | undefined;
  for (const err of validateLandingConfig(persistedLandingConfig)) {
    blockers.push(`Landing page link: ${err}`);
  }

  // Landing-link coverage — surface recipients missing a personalized value
  // BEFORE sending. An unknown placeholder (never resolvable) or a variable
  // empty for the ENTIRE audience is a hard blocker; a missing REQUIRED value
  // is a hard blocker; empty for SOME recipients (optional) is a warning.
  const landingWarnings: string[] = [];
  // Dead-unsubscribe-link migration notice (ASM flag-gate, FG-B1): while the guard is OFF the
  // send is NOT blocked, but flag a legacy {{unsubscribe}} template so it gets migrated to an ASM
  // tag before the guard is turned on. When ON it's already a hard blocker above.
  if (!templateGuardEnabled && emailTemplate?.merge_fields) {
    const forbidden = detectForbiddenUnsubscribeTokens(
      emailTemplate.merge_fields,
    );
    if (forbidden.length > 0) {
      landingWarnings.push(
        `This template still uses a legacy ${forbidden
          .map((f) => `{{${f.token}}}`)
          .join(
            ", ",
          )} unsubscribe link (renders empty on our sends). It will still send, but migrate it to a SendGrid subscription-group (ASM) unsubscribe tag.`,
      );
    }
  }
  // #B1: recipients missing a REQUIRED landing value are isolated from the send (not sent this
  // flight), rather than blocking the whole flight. Operator must acknowledge before confirming.
  const landingExclusions: {
    variable: string;
    empty: number;
    total: number;
  }[] = [];
  if (
    !lookupCoverageLoading &&
    lookupCoverageResult?.success &&
    lookupCoverageResult.landing_link_coverage
  ) {
    const totalAudience = lookupCoverageResult.audience_count ?? 0;
    const requiredVars = new Set(
      requiredLandingVariables(persistedLandingConfig),
    );
    for (const v of lookupCoverageResult.landing_link_coverage) {
      if (!v.known) {
        blockers.push(
          `Landing link references {{${v.variable}}}, which isn't a supported contact field — it would never resolve. Fix the landing page URL or parameter before sending.`,
        );
        continue;
      }
      const isRequired = requiredVars.has(v.variable);
      if (totalAudience > 0 && v.empty === totalAudience) {
        blockers.push(
          `Landing link variable {{${v.variable}}} is empty for all ${totalAudience} recipients — every personalized link would be missing this value. Verify the audience has this field populated before sending.`,
        );
      } else if (v.empty > 0 && isRequired) {
        // Required value missing for SOME recipients → ISOLATE, don't block (#B1).
        // The send path already skips these recipients per-recipient before the
        // provider call (skipped_reason='missing_required_landing_value') and the
        // flight finalizes normally with the rest sent. The operator must explicitly
        // acknowledge the exclusion; we never silently drop them.
        landingExclusions.push({
          variable: v.variable,
          empty: v.empty,
          total: totalAudience,
        });
      } else if (v.empty > 0) {
        landingWarnings.push(
          `${v.empty.toLocaleString()} of ${totalAudience.toLocaleString()} recipients are missing {{${v.variable}}} — their landing link will omit this value.`,
        );
      }
    }
  }

  // #B1: genuine hard-blocks (structural/unknown var, zero-sendable, load/error) still block.
  // A partial required-value exclusion only blocks until the operator acknowledges it.
  const canConfirm =
    blockers.length === 0 && (landingExclusions.length === 0 || exclusionAck);

  // #B1 display. The modal is an ESTIMATE — the authoritative exclusion set is resolved
  // per-recipient at send time. `exclusionMax` (the largest single-variable empty count) is a
  // conservative floor for how many distinct recipients are excluded when multiple vars are required.
  const exclusionTotal = landingExclusions[0]?.total ?? 0;
  const exclusionMax = landingExclusions.reduce(
    (m, e) => Math.max(m, e.empty),
    0,
  );
  const exclusionSendable = Math.max(0, exclusionTotal - exclusionMax);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">
            Confirm flight schedule
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* Flight summary */}
          <div>
            <p className="text-sm text-gray-500">Flight</p>
            <p className="text-base font-medium text-gray-900">{flight.name}</p>
          </div>

          {/* Delivery IP pool. Shown on every send, not only the warmup one, so the operator
              confirms the routing rather than assuming it.

              Driven by a lookup rather than a chain of equality tests: the previous
              version fell through to "Current delivery pool (default)" for anything it
              did not recognise, so adding usls_email_pool_02 would have made this
              confirmation screen state the WRONG pool immediately before a send. An
              unknown key now shows the key itself, which is wrong-looking rather than
              confidently wrong. */}
          {(() => {
            const poolKey =
              (flight as { sendgrid_ip_pool_key?: string | null })
                .sendgrid_ip_pool_key || "default";
            const WARMUP_POOLS: Record<string, { label: string; ip: string }> =
              {
                usls_email_pool_01: {
                  label: "usls-email-pool-01 — warmup",
                  ip: "159.183.13.156",
                },
                usls_email_pool_02: {
                  label: "usls-email-pool-02 — warmup",
                  ip: "159.183.87.246 (o2.newsletter01.2ainstitute.com)",
                },
              };
            const warmup = WARMUP_POOLS[poolKey];
            return (
              <div>
                <p className="text-sm text-gray-500">Delivery IP pool</p>
                <p className="text-base text-gray-900">
                  {warmup
                    ? warmup.label
                    : poolKey === "default"
                      ? "Current delivery pool (default)"
                      : poolKey}
                </p>
                {warmup && (
                  <p className="mt-1 text-xs text-amber-700">
                    Sends from the dedicated warmup IP {warmup.ip} and is tagged{" "}
                    {poolKey.replace(/_/g, "-")}-warmup. A new IP has no sending
                    reputation — confirm this audience is within the volume plan
                    agreed with operations.
                  </p>
                )}
              </div>
            );
          })()}

          {listName && (
            <div>
              <p className="text-sm text-gray-500">Target list</p>
              <p className="text-base text-gray-900">{listName}</p>
            </div>
          )}

          {/* Audience count */}
          <div>
            <p className="text-sm text-gray-500">Audience size</p>
            {audienceLoading ? (
              <p className="text-base text-gray-500 italic">Loading…</p>
            ) : audienceCountFailed ? (
              <div>
                <p className="text-base text-red-600">
                  Failed to load: {audienceResult?.error ?? "unknown error"}
                </p>
                {/* Display-only cached size for dynamic segments. The
                    schedule action stays blocked (dynamic-list sends
                    aren't supported yet) — this is just so the operator
                    knows how big the audience would be. Refreshed hourly
                    by the refresh-dynamic-segment-counts cron job. */}
                {cachedListInfo?.is_dynamic === true &&
                  cachedListInfo.contact_count !== null && (
                    <div className="mt-2 rounded border border-amber-300 bg-amber-50 p-2">
                      <p className="text-sm text-amber-900">
                        Cached size (dynamic segment, display only):{" "}
                        <span className="font-semibold">
                          {cachedListInfo.contact_count.toLocaleString()}
                        </span>{" "}
                        contacts
                      </p>
                      {cachedListInfo.last_calculated && (
                        <p className="text-xs text-amber-800 mt-0.5">
                          Last refreshed{" "}
                          {new Date(
                            cachedListInfo.last_calculated,
                          ).toLocaleString()}
                        </p>
                      )}
                      <p className="text-xs text-amber-800 mt-1">
                        Sending to dynamic segments isn't supported yet —
                        convert to a static list, or pick a different audience.
                      </p>
                    </div>
                  )}
              </div>
            ) : (
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {audienceCount?.toLocaleString() ?? "—"}
                </p>
                <p className="text-xs text-gray-500">eligible recipients</p>
                {(() => {
                  // Email-quality verdict breakdown line (2026-08-23). Shows the
                  // valid / risky / invalid composition of the audience so the
                  // operator can judge the per-flight risky opt-out. Invalid is
                  // ALWAYS excluded; risky is excluded only when the flight opted
                  // out (excludeRisky) — then we also show "· N risky excluded".
                  const vb = audienceResult?.stats?.verdictBreakdown;
                  const invalidExcluded =
                    audienceResult?.stats?.invalidEmailDroppedCount ?? 0;
                  const riskyExcluded =
                    audienceResult?.stats?.riskyEmailDroppedCount ?? 0;
                  const excludeRisky = audienceResult?.stats?.excludeRisky;
                  if (!vb) {
                    return null;
                  }
                  return (
                    <p className="mt-1 text-xs text-gray-600">
                      <span className="font-medium">
                        {(vb.valid ?? 0).toLocaleString()}
                      </span>{" "}
                      valid ·{" "}
                      <span className="font-medium">
                        {(vb.risky ?? 0).toLocaleString()}
                      </span>{" "}
                      risky ·{" "}
                      <span className="font-medium">
                        {invalidExcluded.toLocaleString()}
                      </span>{" "}
                      invalid excluded
                      {excludeRisky ? (
                        <>
                          {" "}
                          ·{" "}
                          <span className="font-medium">
                            {riskyExcluded.toLocaleString()}
                          </span>{" "}
                          risky excluded
                        </>
                      ) : null}
                      {(vb.unvalidated ?? 0) > 0 ? (
                        <>
                          {" "}
                          ·{" "}
                          <span className="font-medium">
                            {(vb.unvalidated ?? 0).toLocaleString()}
                          </span>{" "}
                          not yet checked
                          {autoCheckEnabled === true
                            ? " — checked automatically before sending"
                            : ""}
                        </>
                      ) : null}
                    </p>
                  );
                })()}
                {(() => {
                  const summary = summarizeAudienceStats(audienceResult?.stats);
                  if (!summary || summary.excludedTotal <= 0) {
                    return null;
                  }
                  return (
                    <div className="mt-2 rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-700">
                      <p>
                        <span className="font-medium">
                          {summary.raw.toLocaleString()}
                        </span>{" "}
                        in audience →{" "}
                        <span className="font-medium">
                          {summary.eligible.toLocaleString()}
                        </span>{" "}
                        eligible;{" "}
                        <span className="font-medium">
                          {summary.excludedTotal.toLocaleString()}
                        </span>{" "}
                        excluded before send.
                      </p>
                      {summary.hasBreakdown && (
                        <ul className="mt-1 space-y-0.5">
                          {summary.reasons.map((r) => (
                            <li key={r.key} className="text-gray-600">
                              • {r.count.toLocaleString()} — {r.label}
                            </li>
                          ))}
                        </ul>
                      )}
                      {summary.examples && summary.examples.length > 0 && (
                        <details className="mt-1">
                          <summary className="cursor-pointer text-primary-600 hover:underline">
                            Show excluded examples
                          </summary>
                          <ul className="mt-1 space-y-0.5">
                            {summary.examples.map((e) => (
                              <li key={e.email} className="text-gray-600">
                                {e.email} — {e.label}
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                      {summary.examplesOmitted && (
                        <p className="mt-1 text-gray-400">
                          (large audience — counts only, examples omitted)
                        </p>
                      )}
                    </div>
                  );
                })()}
                {audienceResult?.stats?.suppressionFilterDisabled && (
                  <div className="mt-2 px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-md">
                    <p className="text-xs text-yellow-900">
                      <strong>Warning:</strong> the email suppression filter is
                      currently <strong>disabled</strong> on the server
                      (DISABLE_EMAIL_SUPPRESSION_FILTER kill-switch is active).
                      This send WILL include any suppressed addresses in the
                      audience. Verify with an admin before confirming.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {mode === "send_now" ? (
            <div>
              <p className="text-sm text-gray-500">Send time</p>
              <p className="text-base text-gray-900 font-medium">
                Immediately on confirm
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                This bypasses the scheduler. Confirming starts the send right
                now; the dialog will stay open while emails are handed off to
                SendGrid (typically 30-60 seconds).
              </p>
            </div>
          ) : flight.scheduled_send_at ? (
            <div>
              <p className="text-sm text-gray-500">Scheduled send</p>
              <p className="text-base text-gray-900">
                {formatScheduledDateTime(
                  flight.scheduled_send_at,
                  (flight.metadata as Record<string, unknown> | undefined)
                    ?.send_timezone as string | undefined,
                )}
              </p>
            </div>
          ) : null}

          {/* Landing-link coverage — informational warnings for recipients
              missing a personalized value (e.g. a partner ZID). Empty-for-all
              and unknown-placeholder cases are hard blockers above; these are
              the partial-coverage "know before you send" flags. */}
          {landingWarnings.length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
              <p className="text-sm font-semibold text-amber-800 mb-1">
                Landing link — missing values
              </p>
              <ul className="list-disc list-inside space-y-0.5">
                {landingWarnings.map((w, i) => (
                  <li key={i} className="text-xs text-amber-700">
                    {w}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* #B1: required landing value missing for SOME recipients → they are excluded from
              this flight (not sent). Prominent, acknowledgement-gated — not a hard block. */}
          {landingExclusions.length > 0 && (
            <div className="rounded-md border border-red-300 bg-red-50 p-3">
              <p className="text-sm font-semibold text-red-800 mb-1">
                Some recipients will be excluded from this flight
              </p>
              <ul className="list-disc list-inside space-y-0.5 mb-2">
                {landingExclusions.map((e, i) => (
                  <li key={i} className="text-xs text-red-700">
                    {e.empty.toLocaleString()} of {e.total.toLocaleString()}{" "}
                    recipients are missing required{" "}
                    <code>{`{{${e.variable}}}`}</code> and{" "}
                    <strong>will not be sent this flight</strong>.
                  </li>
                ))}
              </ul>
              <p className="text-xs text-red-700 mb-2">
                {exclusionSendable.toLocaleString()} recipients remain sendable.
                Review the audience if you expected these values to resolve. The
                final excluded set is determined per-recipient at send time.
              </p>
              <label className="flex items-start gap-2 text-xs text-red-900">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 flex-shrink-0"
                  checked={exclusionAck}
                  onChange={(e) => setExclusionAck(e.target.checked)}
                />
                <span>
                  I understand these recipients will be excluded from this
                  flight.
                </span>
              </label>
            </div>
          )}

          {/* Lookup coverage — only renders when the flight uses any
              sourceType='lookup' mappings. Each entry shows per-mapping
              bucket counts and surfaces validation flags. Red flags are
              also pushed into `blockers` above to disable the Send
              button, so this is mainly informational. */}
          {lookupCoverageLoading && (
            <div>
              <p className="text-sm text-gray-500">Lookup coverage</p>
              <p className="text-base text-gray-500 italic">Loading…</p>
            </div>
          )}
          {!lookupCoverageLoading &&
            lookupCoverageResult?.success &&
            lookupCoverageResult.lookup_coverage &&
            lookupCoverageResult.lookup_coverage.length > 0 && (
              <div>
                <p className="text-sm text-gray-500 mb-2">Lookup coverage</p>
                <div className="space-y-2">
                  {lookupCoverageResult.lookup_coverage.map((c) => {
                    const totalAudience =
                      lookupCoverageResult.audience_count ?? 0;
                    const resolvedPct =
                      totalAudience === 0
                        ? 0
                        : Math.round(
                            ((c.matched + c.default_used) / totalAudience) *
                              100,
                          );
                    const hasRed =
                      !c.lookup_exists ||
                      !c.value_column_exists ||
                      !c.contact_key_field_exists ||
                      (c.resolution_mode === "default_row" &&
                        !c.default_row_exists) ||
                      c.filter_columns_exist.some((ok) => !ok);
                    const hasAmber =
                      !hasRed && totalAudience > 0 && resolvedPct < 95;
                    return (
                      <div
                        key={c.mapping_template_field}
                        className={`rounded-md p-3 border text-sm ${
                          hasRed
                            ? "bg-red-50 border-red-200"
                            : hasAmber
                              ? "bg-amber-50 border-amber-200"
                              : "bg-green-50 border-green-200"
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <code className="font-mono text-xs">{`{{${c.mapping_template_field}}}`}</code>
                          {hasRed ? (
                            <span className="text-xs font-semibold text-red-800">
                              Action needed
                            </span>
                          ) : hasAmber ? (
                            <span className="text-xs font-semibold text-amber-800">
                              {resolvedPct}% resolved
                            </span>
                          ) : (
                            <span className="text-xs font-semibold text-green-800">
                              {resolvedPct === 0 && totalAudience === 0
                                ? "Audience empty"
                                : c.resolution_mode === "default_row"
                                  ? `${resolvedPct}% bundle resolved`
                                  : `${resolvedPct}% resolved`}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-700 grid grid-cols-5 gap-1">
                          <div>
                            <div className="text-gray-500">Matched</div>
                            <div className="font-semibold tabular-nums">
                              {c.matched}
                            </div>
                          </div>
                          <div>
                            <div className="text-gray-500">Filter excl.</div>
                            <div className="font-semibold tabular-nums">
                              {c.filter_excluded}
                            </div>
                          </div>
                          <div>
                            <div className="text-gray-500">Default</div>
                            <div className="font-semibold tabular-nums">
                              {c.default_used}
                            </div>
                          </div>
                          <div>
                            <div className="text-gray-500">Fallback</div>
                            <div className="font-semibold tabular-nums">
                              {c.fallback_used}
                            </div>
                          </div>
                          <div>
                            <div className="text-gray-500">Empty key</div>
                            <div className="font-semibold tabular-nums">
                              {c.empty_key}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

          {/* Blocking issues */}
          {blockers.length > 0 && !audienceLoading && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-red-800 mb-2">
                Cannot schedule yet
              </h3>
              <ul className="space-y-1 text-sm text-red-700 list-disc list-inside">
                {blockers.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!canConfirm}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {audienceLoading
              ? "Loading…"
              : mode === "send_now"
                ? "Confirm and send now"
                : "Confirm and schedule"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PreSendConfirmationModal;
